import {
  Badge,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Spinner,
  cn,
} from '@design-system';
import { WagDisclosure } from './wag-disclosure';
import { BgaEligibilityDialog } from './bga-eligibility-dialog';

import { Check } from 'lucide-react';

import { FREEMIUM_BGA_SPONSORED_TEXT } from '@shared';

import type { FreemiumTierConfig } from '@shared';

function formatTierPrice(config: FreemiumTierConfig): string {
  if (config.priceModel === 'free') return '$0';
  if (config.bgaSponsorable) return `$0 - $${config.priceUsd}`;

  return `$${config.priceUsd}`;
}

interface TierCardProps {
  config: FreemiumTierConfig;
  onSelect: () => void;
  isPending: boolean;
  isLoading: boolean;
}

export function TierCard({ config, onSelect, isPending, isLoading }: TierCardProps) {
  const isFeatured = !!config.recommended;

  return (
    <Card
      className={cn('relative flex h-full flex-col shadow-none', {
        'border-primary bg-primary text-primary-foreground': isFeatured,
      })}
    >
      {isFeatured && (
        <Badge
          variant="outline"
          className="text-primary border-primary bg-background absolute -top-3 left-1/2 -translate-x-1/2"
        >
          Most popular
        </Badge>
      )}

      <CardHeader>
        <CardTitle className={cn('text-base', { 'text-primary-foreground': isFeatured })}>
          {config.name}
        </CardTitle>
        <div className="mt-2 flex items-baseline gap-1.5">
          <span
            className={cn('text-primary text-3xl font-bold tracking-tight', {
              'text-primary-foreground': isFeatured,
            })}
          >
            {formatTierPrice(config)}
          </span>
          {config.priceSuffix && (
            <span
              className={cn('text-muted-foreground text-sm', {
                'text-primary-foreground/70': isFeatured,
              })}
            >
              {config.priceSuffix}
            </span>
          )}
        </div>

        {config.bgaSponsorable && (
          <div
            className={cn('mt-2 rounded-lg border px-2.5 py-2 text-sm leading-relaxed', {
              'border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground':
                isFeatured,
              'border-primary/30 bg-primary/5 text-foreground': !isFeatured,
            })}
          >
            <p className="font-semibold">
              {FREEMIUM_BGA_SPONSORED_TEXT}
              {', '}
              <BgaEligibilityDialog
                triggerClassName={cn({ 'text-primary': !isFeatured })}
              />
            </p>
          </div>
        )}
      </CardHeader>

      <CardContent className="flex-1">
        <ul className="space-y-2.5">
          {config.features.map(feature => (
            <li
              key={feature}
              className="flex items-start gap-2 text-sm"
            >
              <Check
                className={cn('text-primary mt-0.5 size-4 shrink-0', {
                  'text-primary-foreground': isFeatured,
                })}
              />
              <span className={cn('text-foreground', { 'text-primary-foreground/90': isFeatured })}>
                {feature}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>

      <CardFooter className="flex-col gap-6">
        <Button
          size="lg"
          onClick={onSelect}
          disabled={isPending}
          className={cn('w-full', { 'text-primary bg-white hover:bg-white/90': isFeatured })}
        >
          {isLoading && <Spinner />}
          {config.ctaLabel}
        </Button>
        <WagDisclosure
          className={cn(
            'text-muted-foreground invisible hidden text-[10px] leading-relaxed lg:block',
            {
              'text-primary-foreground/70': isFeatured,
              'visible block': config.isLicensed,
            },
          )}
        />
      </CardFooter>
    </Card>
  );
}
